import * as React from 'react';
import { createFilterOptions, Autocomplete as MuiAutocomplete, TextField, Typography } from '@mui/material';

export type Option = {
  label: string;
  value: string;
} | null;

interface AutocompleteProps {
  label: string;
  value: Option;
  options: Option[];
  loading?: boolean;
  disabled?: boolean;
  testId?: string;
  setValue: (value: (Option)) => void;
  className?: string;
  onInputChange?: (event: React.SyntheticEvent, value: string) => void;
  freeSolo?: boolean;
}

const filter = createFilterOptions<Option>();

export default function Autocomplete({
  label,
  options,
  value,
  loading = false,
  disabled = false,
  testId,
  setValue,
  className,
  onInputChange,
  freeSolo,
}: AutocompleteProps) {
  return (
    <MuiAutocomplete
      value={value || null}
      onChange={(__, newValue) => {
        if (typeof newValue === 'string') {
          setValue({ label: newValue, value: newValue });
        }
        else {
          setValue(newValue);
        }
      }}
      size='small'
      options={options}
      renderOption={(props, option) => (
        <Typography
          {...props}
          key={option?.value}
          className='fs-mask'
          sx={{
            pl: 1,
            pb: 1,
            opacity: option?.label === 'No options' || option?.label === 'Loading' ? 0.5 : 1,
            pointerEvents: option?.label === 'No options' || option?.label === 'Loading' ? 'none' : 'auto',
            cursor: 'pointer',
          }}
        >
          {option?.label}
        </Typography>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          inputProps={{
            ...params.inputProps,
            onKeyDown: (e) => {
              if (e.key === 'Enter' && freeSolo) {
                e.stopPropagation();
              }
            },
          }}
        />
      )}
      filterOptions={(options1, params) => {
        const { inputValue } = params;
        const filtered = filter(options1, params);

        const hasMatches = options1.some((option) => option?.label?.toLowerCase()
          .includes(inputValue.toLowerCase()));

        // If user is typing and no matches, show loading or no options message
        if (inputValue !== '' && !hasMatches && freeSolo) {
          if (loading) {
            return [{ label: 'Loading', value: 'Loading' }];
          }
          else {
            return [{ label: 'No options', value: 'No options' }];
          }
        }

        return filtered;
      }}
      multiple={false}
      fullWidth
      limitTags={1}
      loading={loading}
      disabled={disabled}
      data-testid={testId}
      className={className}
      freeSolo={freeSolo}
      onInputChange={onInputChange}
    />
  );
}
